import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
  faCircleCheck,
  faCircleInfo,
  faLightbulb,
  faNoteSticky,
  faTriangleExclamation,
} from '@crisp/crisp-ui/icons/classic/regular';
import { cn } from '../../lib/cn';
import styles from './Callout.module.css';

export type CalloutType = 'info' | 'note' | 'tip' | 'warning' | 'check';

const configs: Record<
  CalloutType,
  { icon: IconDefinition; title: string; variant: 'default' | 'destructive' }
> = {
  info: { icon: faCircleInfo, title: 'Info', variant: 'default' },
  note: { icon: faNoteSticky, title: 'Note', variant: 'default' },
  tip: { icon: faLightbulb, title: 'Tip', variant: 'default' },
  warning: { icon: faTriangleExclamation, title: 'Warning', variant: 'destructive' },
  check: { icon: faCircleCheck, title: 'Success', variant: 'default' },
};

export interface CalloutProps {
  type?: CalloutType;
  title?: string;
  children?: React.ReactNode;
  className?: string;
}

export const Callout: React.FC<CalloutProps> = ({
  type = 'note',
  title,
  children,
  className = '',
}) => {
  const config = configs[type] || configs.note;

  return (
    <Alert
      role="note"
      variant={config.variant}
      className={cn(styles.callout, styles[type] ?? styles.note, className)}
    >
      <FontAwesomeIcon icon={config.icon} />
      <AlertTitle>{title || config.title}</AlertTitle>
      {children ? <AlertDescription className={styles.body}>{children}</AlertDescription> : null}
    </Alert>
  );
};

export default Callout;
